import { REPORTS_DASHBOARD_HEADERS as HEADERS } from "../../../constants/analyticsReports";

// ReportsDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Menu,
  MenuItem,
  TextField,
  FormControl,
  Select,
  InputLabel,
  InputAdornment,
  Divider,
  Chip,
} from "@mui/material";
import ApplicationStatusChip from "../../../components/StatusChip/ApplicationStatusChip";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useLocation, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "./reports.scss";
import Label from "../../../components/Label/Label";
import CAMHeader from "../../../components/Header/CAMHeader";
import CAMDrawer from "../../../components/LeftSideBar/CAMDrawer";
import InvestigatorContainer from "../../../components/contsiners/InvestigatorContainer";
import DropdownsBar from "../header/ProductSiteUnitSelector";
import TabsBar from "../header/TabsNav";
import BreadcrumbsBar from "../header/BreadcrumbNav";
import Pagination from "../../../components/Pagination/index";
import {
  fetchReportsList,
  resetGenerateReport,
} from "../../../redux/reports/actions";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CustomizedSnackbars from "../../../components/Snackbars/CustomizedSnackbars";
import { hasPermission } from "../../../utils/permissions";
import {
  PERMISSION_MODULES,
  REPORT_PERMISSIONS,
} from "../../../constants/permissions";

const NoResults = ({ message = "No Reports found." }) => (
  <Box py={6} textAlign="center">
    <Label text={message} />
  </Box>
);

const ReportsDashboard = () => {
  const canCreateReport = hasPermission(PERMISSION_MODULES.REPORT, REPORT_PERMISSIONS.CREATE_NEW_REPORT);
  const canEditDraft = hasPermission(PERMISSION_MODULES.REPORT, REPORT_PERMISSIONS.EDIT_DRAFT_REPORT);
  const canDownloadReport = hasPermission(PERMISSION_MODULES.REPORT, REPORT_PERMISSIONS.DOWNLOAD_REPORT);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { productsAndSitesListData = [] } = useSelector((state) => {
    const list = state?.viz?.productsAndSitesList;
    return {
      productsAndSitesListData: Array.isArray(list?.data) ? list.data : [],
    };
  });

  const generateReportResponse = useSelector(
    (state) => state.reports.generatedReport,
  );

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    if (!location.state?.fromGenerate) return;

    if (generateReportResponse?.loader) {
      setSnackbar({
        open: true,
        message: "Generating report...",
        severity: "info",
      });
    }

    if (generateReportResponse?.apiError) {
      setSnackbar({
        open: true,
        message: generateReportResponse.message || "Failed to generate report",
        severity: "error",
      });
      dispatch(resetGenerateReport());
    }

    if (generateReportResponse?.apiSuccess) {
      setSnackbar({
        open: true,
        message: "Report generated successfully",
        severity: "success",
      });
      dispatch(resetGenerateReport());
    }
  }, [generateReportResponse, location.state]);

  const productList = useMemo(
    () =>
      productsAndSitesListData.map((p) => ({
        id: p.product_id,
        name: p.product_name,
      })),
    [productsAndSitesListData],
  );

  const reportsList = useSelector((state) => state.reports.reportsList || {});
  const { loader, apiError, message, data = {} } = reportsList;

  useEffect(() => {
    if (!apiError) return;
    setSnackbar({
      open: true,
      message: message || "Unable to load reports.",
      severity: "error",
    });
  }, [apiError, message]);

  const reportRows = data?.result || [];
  const totalPages = data?.total_pages || 1;

  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);

  const [filters, setFilters] = useState({
    reportName: "",
    productId: "",
    startDate: null,
    endDate: null,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const open = Boolean(anchorEl);

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (isNaN(date)) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const fetchReports = (withFilters = false) => {
    const payload = {
      page_number: page,
      user_id: localStorage.getItem("userid") || "",
      report_name: filters.reportName,
      product_id: filters.productId,
      start_date: formatDate(filters.startDate),
      end_date: formatDate(filters.endDate),
      withFilters,
    };
    dispatch(fetchReportsList(payload));
  };

  useEffect(() => {
    fetchReports(false);
  }, [dispatch, page]);

  const handleSearch = () => {
    setPage(1);
    fetchReports(true);
  };

  const handleReset = () => {
    setFilters({
      reportName: "",
      productId: "",
      startDate: null,
      endDate: null,
    });
    setPage(1);
    fetchReports(false);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleAddReport = () => {
    if (!canCreateReport) return;
    navigate("/analytics/reports/create-report");
  };

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedReport(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReport(null);
  };

  const handleDownload = async () => {
    if (!canDownloadReport) return;
    handleMenuClose();

    if (selectedReport?.report_id && selectedReport?.report_name) {
      const fileUrl = `${process.env.REACT_APP_APIBASE}/viz-documents/${localStorage.userid}_${selectedReport.report_id}.docx`;
      const fileName = `${selectedReport.report_name}_${selectedReport.created_on}.docx`;

      try {
        const response = await fetch(fileUrl, { method: "GET" });
        if (!response.ok) throw new Error("Failed to fetch file");

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download error:", error);
        alert("Unable to download file.");
      }
    } else {
      alert("File not available for download.");
    }
  };

  const handleView = () => {
    handleMenuClose();
    if (selectedReport?.report_id) {
      navigate("/analytics/reports/view-report", {
        state: { report: selectedReport },
      });
    }
  };

  const handleEdit = () => {
    if (!canEditDraft) return;
    handleMenuClose();
    if (selectedReport?.report_id) {
      navigate(`/analytics/reports/edit-report`, {
        state: {
          report_id: selectedReport.report_id,
          mode: "edit",
        },
      });
    }
  };

  const renderStatus = (status) => {
    if (!status) return <Label text="-" />;

    if (status.includes("%")) {
      return (
        <Chip
          label={status}
          size="small"
          sx={{
            backgroundColor: "#DADADA",
            color: "#black",
            fontWeight: 600,
            width: "120px",
            fontSize: "14px",
          }}
        />
      );
    }

    if (status.toLowerCase() === "completed") {
      return (
        <Chip
          label="Completed"
          size="small"
          sx={{
            backgroundColor: "#C8FACC",
            color: "#37B349",
            fontWeight: 600,
            width: "120px",
            fontSize: "14px",
          }}
        />
      );
    }

    if (status.toLowerCase() === "draft") {
      return (
        <ApplicationStatusChip
          status="Draft"
          sx={{
            width: "120px",
            fontSize: "14px",
          }}
        />
      );
    }

    return <Label text={status} />;
  };

  const renderTable = (data) => {
    if (loader) {
      return (
        <Box py={6} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      );
    }

    if (apiError) {
      return null;
    }

    if (!reportRows.length) return <NoResults />;

    return (
      <TableContainer sx={{ height: "100%" }}>
        <Table stickyHeader size="small" aria-label="Reports table">
          <TableHead>
            <TableRow
              sx={{
                height: 44,
                "& th": { padding: "0px 10px", lineHeight: "normal" },
              }}
            >
              {HEADERS.map((head, idx) => (
                <TableCell
                  key={idx}
                  sx={{ backgroundColor: "#E9E9E9 !important" }}
                >
                  <Label text={head} fontSize="14px" fontWeight="700" />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {reportRows.map((row, index) => (
              <TableRow
                key={row?.report_id ?? index}
                sx={{ "& td": { padding: "4px 8px" } }}
              >
                 <TableCell>{data?.page_size * (data?.page - 1) + index + 1}</TableCell>
                <TableCell>
                  <Label text={row?.report_name} color="#1B72B7" />
                </TableCell>
                <TableCell>
                  <Label text={row?.created_on} />
                </TableCell>
                <TableCell>
                  <Label text={row?.user_name} />
                </TableCell>
                <TableCell>
                  <Label text={row?.product_name || "-"} />
                </TableCell>
                <TableCell>
                  <Label text={row?.total_batches || "-"} />
                </TableCell>
                <TableCell>
                  <Label text={row?.total_paramters || "-"} />
                </TableCell>
                <TableCell>{renderStatus(row?.report_status)}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, row)}
                    disabled={
                      !(
                        (row?.report_status?.toLowerCase() === "draft" && canEditDraft) ||
                        (row?.report_status?.toLowerCase() === "completed" && canDownloadReport)
                      )
                    }
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const getMenuItems = () => {
    if (!selectedReport) return [];
    const status = selectedReport?.report_status?.toLowerCase() || "";

    if (status === "draft") {
      return canEditDraft ? [{ label: "Edit", action: handleEdit, disabled: false }] : [];
    }
    if (status === "completed") {
      return canDownloadReport ? [{ label: "Download", action: handleDownload, disabled: false }] : [];
    }
    if (status.includes("%")) {
      return [];
    }
    return [];
  };

  return (
    <div>
      <CAMHeader />
      <CAMDrawer />
      <InvestigatorContainer>
        <TabsBar />
        <DropdownsBar disabledFromParent={true} />

        <Grid
          container
          alignItems="center"
          spacing={2}
          justifyContent="space-between"
          pr={2}
          pb={1}
        >
          {/* <Grid item sm={12} md={1} sx={{ paddingRight: "0px !important" }}>
          </Grid> */}

          <Grid
            item
            sm={12}
            md={12}
            justifyContent={"flex-end"}
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              paddingLeft: "0px !important",
              flexWrap: "wrap",
            }}
          >
            <TextField
              label="Report Name"
              size="small"
              value={filters.reportName}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, reportName: e.target.value }))
              }
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Product</InputLabel>
              <Select
                value={filters.productId}
                label="Product"
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, productId: e.target.value }))
                }
              >
                <MenuItem value="">All</MenuItem>
                {productList?.map((prod) => (
                  <MenuItem key={prod.id} value={prod.id}>
                    {prod.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box
              sx={{ display: "flex", gap: 1 }}
              className="date-range-picker-batches-dashboard"
            >
              {[
                ["startDate", "From"],
                ["endDate", "To"],
              ].map(([key, placeholder]) => (
                <DatePicker
                  showYearDropdown
                  scrollableYearDropdown
                  dateFormat="dd-MMM-yyyy"
                  yearDropdownItemNumber={50}
                  popperClassName="custom-datepicker-popper"
                  key={key}
                  selected={filters[key]}
                  onChange={(date) =>
                    setFilters((prev) => ({ ...prev, [key]: date }))
                  }
                  selectsStart={key === "startDate"}
                  selectsEnd={key === "endDate"}
                  startDate={
                    filters.startDate instanceof Date ? filters.startDate : null
                  }
                  endDate={
                    filters.endDate instanceof Date ? filters.endDate : null
                  }
                  minDate={
                    key === "endDate" && filters.startDate instanceof Date
                      ? filters.startDate
                      : undefined
                  }
                  maxDate={
                    key === "startDate"
                      ? filters.endDate instanceof Date
                        ? filters.endDate
                        : new Date()
                      : new Date()
                  }
                  placeholderText={placeholder}
                  dateFormat="dd-MMM-yyyy"
                  fixedHeight
                  renderCustomHeader={({
                    date,
                    decreaseMonth,
                    increaseMonth,
                    changeYear,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                  }) => (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 1.5,
                        py: 1,
                        bgcolor: "primary.main",
                        color: "common.white",
                        borderRadius: "8px 8px 0 0",
                      }}
                    >
                      <IconButton
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                        sx={{ color: "common.white", p: 0.5 }}
                        size="small"
                      >
                        <ArrowBackIosIcon fontSize="small" />
                      </IconButton>

                      <Box
                        sx={{
                          mx: 1,
                          fontWeight: 500,
                          cursor: "default",
                          width: 80,
                          textAlign: "center",
                        }}
                      >
                        <Label
                          text={date.toLocaleString("default", {
                            month: "long",
                          })}
                          color="#ffff"
                          fontSize={16}
                          fontWeight={600}
                        />
                      </Box>

                      <Select
                        value={date.getFullYear()}
                        onChange={(e) => changeYear(Number(e.target.value))}
                        size="small"
                        onMouseDown={(e) => e.stopPropagation()} // prevent closing
                        onClick={(e) => e.stopPropagation()} // prevent closing
                        sx={{
                          alignItems: "center",
                          textAlign: "center",
                          fontSize: 13,
                          color: "text.primary",
                          bgcolor: "background.paper",
                          borderRadius: 1,
                          // "& .MuiSelect-select": { py: 0.25, px: 1 },
                        }}
                      >
                        {Array.from(
                          { length: 50 },
                          (_, i) => new Date().getFullYear() - i,
                        ).map((y) => (
                          <MenuItem key={y} value={y}>
                            {y}
                          </MenuItem>
                        ))}
                      </Select>

                      <IconButton
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                        sx={{ color: "common.white", p: 0.5 }}
                        size="small"
                      >
                        <ArrowForwardIosIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  customInput={
                    <TextField
                      size="small"
                      sx={{
                        width: 140,
                        backgroundColor: "#fff",
                        borderRadius: "6px",
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarTodayIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        readOnly: true,
                      }}
                    />
                  }
                />
              ))}
            </Box>

            <Button variant="contained" color="primary" onClick={handleSearch}>
              <Label text="Search" color="inherit" />
            </Button>

            <Button
              variant="outlined"
              onClick={handleReset}
              sx={{ textTransform: "none", minWidth: 80 }}
            >
              <Label text="Reset" color="inherit" />
            </Button>

            {canCreateReport && <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddReport}
              sx={{
                textTransform: "none",
                backgroundColor: "#3882DB",
                color: "#fff",
                "&:hover": { backgroundColor: "#2f6fb8" },
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              <Label text="New Report" color="#fff" />
            </Button>}
          </Grid>
        </Grid>
        <Divider sx={{ mx: 2 }} />
        <Box sx={{ py: 0.5 }}>
          <BreadcrumbsBar />
        </Box>
        <Divider sx={{ mx: 2 }} />
        <Box className="report-container">
          <Grid container sx={{ height: "100%" }}>
            <Grid item xs={12} sx={{ height: "calc(100vh - 240px)" }}>
              <Box className="report-card-wrapper">
                <Box className="report-scrollable-wrapper">{renderTable(data)}</Box>

                <Box
                  className="sticky-footer"
                  display="flex"
                  justifyContent="center"
                  py={1}
                >
                  {!loader && (
                    <Pagination
                      page={page}
                      count={totalPages}
                      onChange={handlePageChange}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </InvestigatorContainer>

      <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
        {getMenuItems().map((item, idx) => (
          <MenuItem
            key={idx}
            onClick={!item.disabled ? item.action : undefined}
            disabled={item.disabled}
          >
            <Label text={item.label} color={item.disabled ? "#999" : "#000"} />
          </MenuItem>
        ))}
      </Menu>
      {snackbar.open && (
        <CustomizedSnackbars
          severity={snackbar.severity}
          state={snackbar.open}
          text={snackbar.message}
          handleClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        />
      )}
    </div>
  );
};

export default ReportsDashboard;
